import { type RefObject, useCallback, useEffect, useRef, useState } from 'react'

type ScrollPosition = {
  left: number
  top: number
}

export function useRafScrollPosSync(
  scrollContainerRef: RefObject<HTMLDivElement | null>,
  enabled: boolean
) {
  const [scrollPos, setScrollPos] = useState<ScrollPosition>({
    left: 0,
    top: 0,
  })
  const enabledRef = useRef(false)
  const latestScrollPosRef = useRef<ScrollPosition>({ left: 0, top: 0 })
  const animationFrameRef = useRef<number | null>(null)

  enabledRef.current = enabled

  const scheduleScrollPosSync = useCallback((container: HTMLDivElement) => {
    latestScrollPosRef.current = {
      left: container.scrollLeft,
      top: container.scrollTop,
    }

    if (!enabledRef.current || animationFrameRef.current !== null) {
      return
    }

    animationFrameRef.current = requestAnimationFrame(() => {
      animationFrameRef.current = null
      if (!enabledRef.current) return

      const nextScrollPos = latestScrollPosRef.current
      setScrollPos((prev) =>
        prev.left === nextScrollPos.left && prev.top === nextScrollPos.top
          ? prev
          : nextScrollPos
      )
    })
  }, [])

  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container) return

    const syncScroll = () => scheduleScrollPosSync(container)
    container.addEventListener('scroll', syncScroll, { passive: true })
    return () => {
      container.removeEventListener('scroll', syncScroll)
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current)
        animationFrameRef.current = null
      }
    }
  }, [scrollContainerRef, scheduleScrollPosSync])

  useEffect(() => {
    const container = scrollContainerRef.current
    if (enabled && container) scheduleScrollPosSync(container)
  }, [enabled, scheduleScrollPosSync, scrollContainerRef])

  return scrollPos
}
